// [guanxing patch] M-SW55：跨端图片保存（修 iPhone 点「把这片星空带走 / 太阳系下载」
// 无法正常保存、乃至崩溃的问题）。
//
// 旧写法（M-SW38/39/41）：把成图当 data:URL 直接塞进 <a href="data:..." download>。
//   ① iOS Safari 对 data:URL 的 download 属性不可靠——常被当作「打开图片」，
//      用户只能长按「存储到照片」，于是图进了相册（用户报告的现象）；
//   ② click() 后同步 removeChild 在 Safari 上可能取消本次跳转；
//   ③ 数 MB 的 base64 直接作为 <a href> 也会显著抬高内存。
//
// 本模块统一：成图先转 Blob（避免大 base64 字符串进入 DOM）→
//   · iOS/iPadOS：走 Web Share Level 2 的 navigator.share({ files })，弹系统分享面板，
//     用户可选「存储图像」（存照片）或「存到文件」（存文件、保留文件名）；
//   · 其它端：blob:URL + <a download>，延迟移除锚点并 revoke objectURL（避开 Safari
//     同步移除取消跳转的坑）。桌面/安卓行为与旧版一致（仍落「下载」）。

const FALLBACK_MIME = 'image/png'

// data:URL → Blob。手写解码（不经 fetch）：同步、无网络、对 data:URL 最稳。
export function dataUrlToBlob (dataUrl) {
  const comma = dataUrl.indexOf(',')
  const meta = comma >= 0 ? dataUrl.slice(0, comma) : ''
  const body = comma >= 0 ? dataUrl.slice(comma + 1) : ''
  const mimeMatch = /data:([^;,]+)/.exec(meta)
  const mime = mimeMatch ? mimeMatch[1] : FALLBACK_MIME
  if (!/;base64/i.test(meta)) {
    // 非 base64（罕见）：按 percent-encoding 解码
    return new Blob([decodeURIComponent(body)], { type: mime })
  }
  const bin = atob(body)
  const len = bin.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

// iOS / iPadOS 判定（含 iPadOS 13+ 的桌面级 UA：platform=MacIntel 且支持多点触控）。
export function isIOS () {
  try {
    const ua = navigator.userAgent || ''
    if (/iPad|iPhone|iPod/.test(ua)) return true
    if (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1) return true
    return false
  } catch (e) {
    return false
  }
}

// 浏览器能否分享该文件（Web Share Level 2；iOS 15+ Safari 支持，旧版/部分 WebView 不支持）。
export function canShareFile (file) {
  try {
    return !!(navigator.share && navigator.canShare && navigator.canShare({ files: [file] }))
  } catch (e) {
    return false
  }
}

// blob:URL 下载（非 iOS 主路径）。
export function downloadBlob (blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  // Safari 在 click 后同步移除锚点会取消跳转；延迟移除并释放 objectURL。
  setTimeout(function () {
    if (a.parentNode) a.parentNode.removeChild(a)
    URL.revokeObjectURL(url)
  }, 4000)
}

function canvasToBlob (canvas) {
  return new Promise(function (resolve, reject) {
    canvas.toBlob(function (b) {
      if (b) resolve(b)
      else reject(new Error('canvas.toBlob 返回空'))
    }, FALLBACK_MIME)
  })
}

// 统一保存入口。source 可为 data:URL 字符串 / Blob / canvas。
//   返回 { method: 'share'|'download', aborted?: true, blocked?: true, error? }
//   · aborted —— 用户在系统分享面板放弃（应尊重，不再继续后续保存）；
//   · blocked —— 分享被系统拒绝（典型：同一次手势里发起的第二次分享没有
//     transient activation），调用方可提示用户「再点一次」。
export async function saveImage (source, filename) {
  const name = filename || 'star-map.png'
  let blob
  if (source instanceof Blob) blob = source
  else if (source && typeof source.toBlob === 'function') blob = await canvasToBlob(source)
  else blob = dataUrlToBlob(String(source))
  const type = blob.type || FALLBACK_MIME
  const file = new File([blob], name, { type: type })

  if (isIOS() && canShareFile(file)) {
    try {
      await navigator.share({ files: [file] })
      return { method: 'share' }
    } catch (e) {
      if (e && e.name === 'AbortError') return { method: 'share', aborted: true }
      return { method: 'share', blocked: true, error: e }
    }
  }

  downloadBlob(blob, name)
  return { method: 'download' }
}
